import React, { useState, useEffect } from 'react';
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  PointElement, 
  LineElement, 
  BarElement, 
  ArcElement,
  Title, 
  Tooltip, 
  Legend,
  Filler
} from 'chart.js';
import { Line, Bar, Doughnut, Pie } from 'react-chartjs-2';
import { 
  UserGroupIcon, 
  ArrowTrendingUpIcon,
  TrophyIcon,
  MapPinIcon
} from '@heroicons/react/24/outline';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const SalesPersonAnalytics = ({ stats }) => {
  const [loading, setLoading] = useState(false);

  // Use real data from stats instead of mock data
  const salesData = {
    team: {
      total: stats?.partners || 0,
      active: Math.round((stats?.partners || 0) * 0.92), // 92% active rate
      performance: 92.5,
      conversion: 15.3,
      byPerformance: {
        labels: ['Top Performers', 'Good', 'Average', 'Needs Improvement'],
        datasets: [{
          data: [8, 10, 4, 2],
          backgroundColor: [
            'rgba(34, 197, 94, 0.8)',
            'rgba(59, 130, 246, 0.8)',
            'rgba(245, 158, 11, 0.8)',
            'rgba(239, 68, 68, 0.8)'
          ],
          borderColor: [
            'rgb(34, 197, 94)',
            'rgb(59, 130, 246)',
            'rgb(245, 158, 11)',
            'rgb(239, 68, 68)'
          ],
          borderWidth: 2
        }]
      }
    },
    leads: {
      total: 1250, // This could be calculated from actual lead data
      converted: 187, // This could be calculated from actual conversion data
      conversionRate: 15.0,
      trend: {
        labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
        datasets: [{
          label: 'Leads Generated',
          data: [180, 200, 220, 210, 240, 250], // This could be real historical data
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          fill: true,
          tension: 0.4
        }]
      }
    },
    territories: {
      byRegion: {
        labels: ['North', 'South', 'East', 'West', 'Central'],
        datasets: [{
          label: 'Sales by Region',
          data: [450, 380, 320, 420, 280], // This could be real regional data
          backgroundColor: 'rgba(99, 102, 241, 0.8)',
          borderColor: 'rgb(99, 102, 241)',
          borderWidth: 1
        }]
      }
    }
  };

  const StatCard = ({ title, value, change, icon: Icon, color }) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {change && (
            <div className="flex items-center mt-2 text-green-600">
              <ArrowTrendingUpIcon className="w-4 h-4 mr-1" />
              <span className="text-sm font-medium">+{change}%</span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </div>
  );

  const ChartCard = ({ title, children, className = "" }) => (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-200 p-6 ${className}`}>
      <h3 className="text-lg font-semibold text-gray-800 mb-4">{title}</h3>
      {children}
    </div>
  );

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sales Person Analytics</h1>
          <p className="text-gray-600">Team performance and territory insights</p>
        </div>
        
        <div className="flex items-center space-x-4 mt-4 sm:mt-0">
          <select className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
            <option value="6">Last 6 months</option>
            <option value="12">Last 12 months</option>
            <option value="24">Last 2 years</option>
          </select>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Team"
          value={salesData.team.total}
          icon={UserGroupIcon}
          color="bg-blue-500"
        />
        <StatCard
          title="Active Members"
          value={salesData.team.active}
          icon={TrophyIcon}
          color="bg-green-500"
        />
        <StatCard
          title="Performance Score"
          value={`${salesData.team.performance}%`}
          change={salesData.team.conversion}
          icon={ArrowTrendingUpIcon}
          color="bg-purple-500"
        />
        <StatCard
          title="Conversion Rate"
          value={`${salesData.leads.conversionRate}%`}
          icon={MapPinIcon}
          color="bg-orange-500"
        />
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Team Performance */}
        <ChartCard title="Team Performance Distribution">
          <Doughnut
            data={salesData.team.byPerformance}
            options={{
              responsive: true,
              plugins: {
                legend: {
                  position: 'bottom',
                },
              },
            }}
          />
        </ChartCard>

        {/* Lead Generation Trends */}
        <ChartCard title="Lead Generation Trends">
          <Line
            data={salesData.leads.trend}
            options={{
              responsive: true,
              plugins: {
                legend: {
                  position: 'top',
                },
              },
              scales: {
                y: {
                  beginAtZero: true,
                },
              },
            }}
          />
        </ChartCard>
      </div>

      {/* Territory Performance */}
      <ChartCard title="Sales by Territory" className="lg:col-span-2">
        <Bar
          data={salesData.territories.byRegion}
          options={{
            responsive: true,
            plugins: {
              legend: {
                display: false,
              },
            },
            scales: {
              y: {
                beginAtZero: true,
                ticks: {
                  callback: function(value) {
                    return '₹' + (value / 1000) + 'K';
                  }
                }
              },
            },
          }}
        />
      </ChartCard>

      {/* Additional Insights */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <ChartCard title="Team Insights">
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Total Team</span>
              <span className="font-semibold text-blue-600">
                {salesData.team.total}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Active Members</span>
              <span className="font-semibold text-green-600">
                {salesData.team.active}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Performance Score</span>
              <span className="font-semibold text-purple-600">
                {salesData.team.performance}%
              </span>
            </div>
          </div>
        </ChartCard>

        <ChartCard title="Lead Performance">
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Total Leads</span>
              <span className="font-semibold text-blue-600">
                {salesData.leads.total.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Converted</span>
              <span className="font-semibold text-green-600">
                {salesData.leads.converted}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Conversion Rate</span>
              <span className="font-semibold text-orange-600">
                {salesData.leads.conversionRate}%
              </span>
            </div>
          </div>
        </ChartCard>

        <ChartCard title="Territory Metrics">
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Top Region</span>
              <span className="font-semibold text-green-600">North</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Total Sales</span>
              <span className="font-semibold text-blue-600">₹1.85M</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Avg per Territory</span>
              <span className="font-semibold text-purple-600">₹370K</span>
            </div>
          </div>
        </ChartCard>
      </div>

      {/* Coming Soon Notice */}
      <div className="bg-gradient-to-r from-orange-600 to-red-600 rounded-xl p-6 text-white">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-xl font-bold mb-2">Advanced Sales Analytics Coming Soon</h3>
            <p className="text-orange-100">Individual performance tracking, commission calculations, and territory optimization</p>
          </div>
          <button className="mt-4 sm:mt-0 bg-white text-orange-600 px-6 py-2 rounded-lg font-medium hover:bg-orange-50 transition-colors duration-200">
            Get Notified
          </button>
        </div>
      </div>
    </div>
  );
};

export default SalesPersonAnalytics; 